import { useState, forwardRef } from "react";
import { Button, Row, Col, FormControl } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import {
  copyToClipboard,
  prettifyXml,
  saveToFile,
} from "../../utilities/export";
import {
  generateSessionCardData,
  generateSessionYoutubeCreation,
  generateOBSTemplate,
} from "../../utilities/generate-session-url";
import { YYYYMMDD, GYMapparatusAbbv } from "../../utilities/conversions";
import axios from "axios";
import { alertActions, adminActions } from "../../redux/_actions";

const SNIPPET_TYPE = {
  MATCHCARD: "Match Card",
  YOUTUBE: "YouTube Creation",
  APIJSON: "API JSON",
  APIXML: "API XML",
  OBSTEMPLATE: "OBS Template",
  URLS: "Session URLs",
  LINEUP: "Lineup JSON",
};

export const SessionAdminFunctions = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const {
    MATCHCARD,
    YOUTUBE,
    APIJSON,
    APIXML,
    OBSTEMPLATE,
    URLS,
    LINEUP,
  } = SNIPPET_TYPE;
  const { sessionId } = props;
  const [text, setText] = useState("");
  const [type, setType] = useState(null);
  const session = useSelector((state) => state.admin.sessions.byId[sessionId]);
  const sessionKey = session.sessionKey;
  const sessionTeams = useSelector((state) => state.admin.sessionTeams);
  const lineups = useSelector((state) => state.admin.lineups);
  const sessionApparatus = GYMapparatusAbbv(session.gender);
  const teams = session.sessionTeams.items.map(
    (stId) => sessionTeams.byId[stId].team
  );

  const handleExpandLineups = () => {
    dispatch(alertActions.clear());

    // Get all lineups for this session
    const sessionLineups = session.lineups.items
      .map((id) => lineups.byId[id])
      .filter((lineup) => lineup && lineup.lineupData);

    if (sessionLineups.length === 0) {
      dispatch(alertActions.error("No lineups with data found to expand."));
      return;
    }

    const payload = [];
    let expandedCount = 0;

    sessionLineups.forEach((lineup) => {
      try {
        // Handle both string and already-parsed object
        const lineupData =
          typeof lineup.lineupData === "string"
            ? JSON.parse(lineup.lineupData)
            : lineup.lineupData;

        if (!lineupData || typeof lineupData !== "object") {
          console.error(`Invalid lineupData for lineup ${lineup.id}`);
          return;
        }

        const rotationKeys = Object.keys(lineupData).filter(
          (k) => !isNaN(parseInt(k))
        );

        // Only expand if there's exactly one rotation
        if (rotationKeys.length !== 1) {
          console.log(
            `Skipping lineup ${lineup.id}: found ${rotationKeys.length} rotation keys:`,
            rotationKeys
          );
          return;
        }

        const sourceRotationNum = parseInt(rotationKeys[0], 10) || 1;
        const sourceData = lineupData[rotationKeys[0]];
        const len = sourceData.length;

        console.log(
          `Expanding lineup ${lineup.id}: source rotation ${sourceRotationNum}, ${len} athletes`
        );

        // Create expanded lineup data with rotations 1-6
        // The source rotation keeps its original order (shift 0)
        // Each subsequent rotation drops one athlete (shift +1)
        // Pattern continues wrapping from R6 -> R1
        //
        // Example: Source is R4 with [A, B, C, D, E, F]
        // R4: [A, B, C, D, E, F] (source, shift 0)
        // R5: [B, C, D, E, F, A] (shift 1)
        // R6: [C, D, E, F, A, B] (shift 2)
        // R1: [D, E, F, A, B, C] (shift 3, wrapped from R6)
        // R2: [E, F, A, B, C, D] (shift 4)
        // R3: [F, A, B, C, D, E] (shift 5)
        const expandedData = {};
        for (let rotation = 1; rotation <= 6; rotation++) {
          // Calculate shift relative to source rotation, wrapping R6 -> R1
          const rawShift = (((rotation - sourceRotationNum) % 6) + 6) % 6;
          const shift = rawShift % len;
          const rotatedData = [
            ...sourceData.slice(shift),
            ...sourceData.slice(0, shift),
          ];
          expandedData[rotation] = JSON.parse(JSON.stringify(rotatedData));
        }

        payload.push({
          id: lineup.id,
          _version: lineup._version,
          order: lineup.order,
          lineupData: JSON.stringify(expandedData),
        });
        expandedCount++;
      } catch (e) {
        console.error(`Failed to parse lineupData for lineup ${lineup.id}:`, e);
      }
    });

    if (payload.length === 0) {
      dispatch(
        alertActions.error("No lineups found with a single rotation to expand.")
      );
      return;
    }

    dispatch(adminActions.updateLineupTitles(payload));
    dispatch(
      alertActions.success(
        `Expanded ${expandedCount} lineup(s) to 6 rotations.`
      )
    );
    setText(
      `Expanded ${expandedCount} lineup(s) from single rotation to rotations 1-6.`
    );
    setType("EXPAND");
  };

  function printObjectDetailed(obj) {
    let result = "";

    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        result += `   ${key}: "${obj[key]}",\n`;
      }
    }

    return `{\n${result}}`;
  }

  const handleCopy = (data, type) => {
    copyToClipboard(data);
    dispatch(alertActions.success(`${type} data copied to clipboard.`));
  };

  const handleAsync = async (type) => {
    let urlType = "";

    switch (type) {
      case APIJSON:
        urlType = "json";
        break;
      case APIXML:
        urlType = "xml";
        break;
      default:
        break;
    }

    const req = `https://api.virti.us/session/${sessionKey}/${urlType}`;
    await axios
      .get(req, {
        headers: { "Content-Type": "application/json" },
      })
      .then((response) => {
        const data = response?.data;

        if (data) {
          let formattedData = "";

          if (urlType === "json") {
            formattedData = JSON.stringify(data, null, 2);
          }

          if (urlType === "xml") {
            formattedData = prettifyXml(data);
          }

          handleCopy(formattedData, type);
          setText(formattedData);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleShow = (type) => {
    let cardData = "";
    dispatch(alertActions.clear());
    setType(type);

    switch (type) {
      case YOUTUBE:
        cardData = JSON.stringify(
          generateSessionYoutubeCreation(session),
          null,
          2
        );
        handleCopy(cardData, type);
        setText(cardData);
        break;
      case MATCHCARD:
        cardData = printObjectDetailed(generateSessionCardData(session, teams));
        handleCopy(cardData, type);
        setText(cardData);
        break;
      case APIJSON:
        handleAsync(type);
        break;
      case APIXML:
        handleAsync(type);
        break;
      case OBSTEMPLATE:
        cardData = JSON.stringify(generateOBSTemplate(session), null, 2);
        const date = new Date(session.startAt);
        handleCopy(cardData, type);
        setText(cardData);
        saveToFile(`${YYYYMMDD(date, false)} - ${session.name}.json`, cardData);
        break;
      case LINEUP:
        const timestamp = new Date().toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short",
        });
        const sessionLineups = session.lineups.items
          .map((id) => lineups.byId[id])
          .sort((a, b) => a.order - b.order);

        const lineupData = {
          sessionKey: sessionKey,
          exportedAt: timestamp,
          lineups: sessionLineups,
        };
        cardData = JSON.stringify(lineupData, null, 2);
        handleCopy(cardData, type);
        setText(cardData);
        saveToFile(
          `lineups-${sessionKey}-${new Date()
            .toISOString()
            .replace(/[:.]/g, "-")}.json`,
          cardData
        );
        break;
      case URLS:
        const baseUrl = `https://virti.us/session?s=${sessionKey}`;
        const apiURL = `https://api.virti.us/session/${sessionKey}/`;
        const scoreboardLetters = Array.from({ length: teams.length }, (_, i) =>
          String.fromCharCode("a".charCodeAt(0) + i)
        );
        const modifiers = [
          ...scoreboardLetters.map((letter) => `&scoreboard=${letter}`),
          ...sessionApparatus.map((app) => `&scoreboard=${app.toLowerCase()}`),
          ...sessionApparatus.map(
            (app) => `&scoreboard=${app.toLowerCase()}&theme=dark&size=sm`
          ),
          "&scoreboard=teams",
          "&scoreboard=teamsbyapparatus",
          "&leaderboard",
          "&beta=quad",
        ];
        const apiModifiers = ["json", "xml"];
        cardData = "Session:\n";
        cardData =
          cardData +
          baseUrl +
          "\n\nScoreboards:\n" +
          modifiers.map((mod) => `${baseUrl}${mod}`).join("\n");
        cardData =
          cardData +
          "\n\nAPI:\n" +
          apiModifiers.map((mod) => `${apiURL}${mod}`).join("\n");
        handleCopy(cardData, type);
        setText(cardData);
        break;
      default:
        break;
    }
  };

  const matchData = (
    <>
      <Row style={{ padding: "1rem" }}>
        <Col
          xs={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button variant="secondary" onClick={() => handleShow(MATCHCARD)}>
            Match Card Data
          </Button>
        </Col>
        <Col
          xs={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button variant="secondary" onClick={() => handleShow(YOUTUBE)}>
            Youtube Stream Creation
          </Button>
        </Col>
        <Col
          xs={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button variant="secondary" onClick={() => handleShow(APIJSON)}>
            API Data (JSON)
          </Button>
        </Col>
        <Col
          xs={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button variant="secondary" onClick={() => handleShow(APIXML)}>
            API Data (XML)
          </Button>
        </Col>
      </Row>
      <Row style={{ padding: "1rem" }}>
        <Col
          xs={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button variant="secondary" onClick={() => handleShow(OBSTEMPLATE)}>
            OBS Template
          </Button>
        </Col>
        <Col
          xs={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button variant="secondary" onClick={() => handleShow(URLS)}>
            Session URLs
          </Button>
        </Col>
        <Col
          xs={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button variant="warning" onClick={handleExpandLineups}>
            Expand Lineups
          </Button>
        </Col>
        <Col
          xs={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button variant="secondary" onClick={() => handleShow(LINEUP)}>
            Export Lineup
          </Button>
        </Col>
      </Row>
    </>
  );

  const textArea = (
    <Row style={{ padding: "1rem" }}>
      {type === URLS ? (
        <div
          style={{
            minHeight: "50vh",
            width: "100%",
            padding: "1rem",
            backgroundColor: "#fff",
            border: "1px solid #ced4da",
            borderRadius: ".25rem",
            whiteSpace: "pre-wrap", // Ensures spaces and line breaks are preserved
          }}
        >
          {text.split("\n").map((line, index) => {
            // Use the original line without trimming to preserve leading/trailing spaces
            const isURL = /^(https?:\/\/[^\s]+)$/i.test(line.trim());

            if (line === "") {
              // Render a blank line as <br>
              return (
                <div key={index}>
                  <br />
                </div>
              );
            }

            return (
              <div key={index}>
                {isURL ? (
                  <a
                    href={line.trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {line.trim()}
                  </a>
                ) : (
                  <span>{line}</span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <FormControl
          as="textarea"
          style={{ minHeight: "50vh", padding: "1rem" }}
          value={text ?? ""}
          onChange={(e) => setText(e.target.value)}
        />
      )}
    </Row>
  );

  return (
    <>
      {matchData}
      {textArea}
    </>
  );
});
